import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setTestimonialsState, setTestimonialsLoading } from '../redux/states';
import { fetchTestimonials } from '../utils/api';

/**
 * useTestimonials custom hook to manage testimonial state across components.
 * 
 * @param {boolean} forceFetch - Whether to skip cache and fetch fresh data
 * @returns {Array} List of testimonials
 */
export const useTestimonials = (forceFetch = false) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.stateData);
  const testimonialsState = state?.testimonials || { items: [], lastFetched: 0, loading: false };
  const { items, lastFetched, loading } = testimonialsState;
  const fetchingRef = useRef(false);

  useEffect(() => {
    // Cache for 15 minutes before re-fetching
    const CACHE_DURATION = 15 * 60 * 1000;
    const now = Date.now();
    const isCacheExpired = now - lastFetched > CACHE_DURATION;

    if ((items.length === 0 || isCacheExpired || forceFetch) && !loading && !fetchingRef.current) {
      const getTestimonialsData = async () => {
        fetchingRef.current = true;
        dispatch(setTestimonialsLoading(true));
        try {
          const response = await fetchTestimonials();
          if (response.success && response.data) {
            dispatch(setTestimonialsState(response.data));
          }
        } catch (error) {
          console.error("Failed to fetch testimonials", error);
        } finally {
          dispatch(setTestimonialsLoading(false));
          fetchingRef.current = false;
        }
      };
      getTestimonialsData();
    }
  }, [dispatch, items.length, lastFetched, loading, forceFetch]);

  return items;
};

export default useTestimonials;
